import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useSite } from "../../lib/site";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { PencilSimple, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

export const CONFIG = {
  news: {
    fields: [
      { k: "date", l: "Date", ph: "e.g. 15 Nov 2026" },
      { k: "title", l: "Headline", req: true },
      { k: "body", l: "Description", req: true, area: true },
    ],
    primary: "title", secondary: "body", meta: "date",
  },
  careers: {
    fields: [
      { k: "role", l: "Position", req: true, ph: "e.g. TGT Mathematics" },
      { k: "type", l: "Job type", req: true, ph: "e.g. Full-time" },
      { k: "req", l: "Requirements", req: true, area: true },
    ],
    primary: "role", secondary: "req", meta: "type",
  },
  achievements: {
    fields: [
      { k: "value", l: "Number / highlight", req: true, ph: "e.g. 25+" },
      { k: "label", l: "Label", req: true, ph: "e.g. Years of excellence" },
      { k: "desc", l: "Short description", area: true },
    ],
    primary: "value", secondary: "label",
  },
};

const blank = (kind) => Object.fromEntries(CONFIG[kind].fields.map((f) => [f.k, ""]));

export const ContentManager = ({ kind }) => {
  const cfg = CONFIG[kind];
  const { items, reload } = useSite(kind);
  const [form, setForm] = useState(blank(kind));
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setForm(blank(kind)); setEditId(null); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) await api.put(`/site/${kind}/${editId}`, form);
      else await api.post(`/site/${kind}`, form);
      toast.success(editId ? "Updated — live on the website" : "Added — live on the website");
      reset(); await reload();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this item from the website?")) return;
    try { await api.delete(`/site/${kind}/${id}`); toast.success("Removed"); if (editId === id) reset(); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const edit = (it) => { setEditId(it.id); setForm(Object.fromEntries(cfg.fields.map((f) => [f.k, it[f.k] || ""]))); };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <Card className="lg:col-span-2 p-5 border-zinc-200 h-fit">
        <h3 className="font-display font-bold mb-4">{editId ? "Edit item" : "Add new"}</h3>
        <form onSubmit={save} className="space-y-3">
          {cfg.fields.map((f) => {
            const Comp = f.area ? Textarea : Input;
            return (
              <div key={f.k}>
                <Label>{f.l}{f.req && " *"}</Label>
                <Comp required={f.req} placeholder={f.ph} rows={f.area ? 4 : undefined} className="mt-1"
                  value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  data-testid={`${kind}-input-${f.k}`} />
              </div>
            );
          })}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy} data-testid={`${kind}-save-btn`} className="bg-indigo-700 hover:bg-indigo-800 text-white">
              <Plus size={14} className="mr-1" />{editId ? "Save changes" : "Add"}
            </Button>
            {editId && <Button type="button" variant="outline" onClick={reset} data-testid={`${kind}-cancel-btn`}>Cancel</Button>}
          </div>
        </form>
      </Card>
      <div className="lg:col-span-3 space-y-2">
        {items.length === 0 && <div className="text-sm text-zinc-500">Nothing published yet.</div>}
        {items.map((it) => (
          <Card key={it.id} className={`p-4 border-zinc-200 flex items-start gap-3 ${editId === it.id ? "ring-2 ring-indigo-300" : ""}`} data-testid={`${kind}-row-${it.id}`}>
            <div className="flex-1 min-w-0">
              {cfg.meta && it[cfg.meta] && <div className="text-xs text-zinc-500">{it[cfg.meta]}</div>}
              <div className="font-semibold">{it[cfg.primary]}</div>
              <div className="text-sm text-zinc-600 line-clamp-2">{it[cfg.secondary]}</div>
            </div>
            <button onClick={() => edit(it)} className="p-1.5 text-zinc-500 hover:text-indigo-700 transition-colors" data-testid={`${kind}-edit-${it.id}`}><PencilSimple size={16} /></button>
            <button onClick={() => remove(it.id)} className="p-1.5 text-zinc-500 hover:text-rose-600 transition-colors" data-testid={`${kind}-delete-${it.id}`}><Trash size={16} /></button>
          </Card>
        ))}
      </div>
    </div>
  );
};
